/* First-run onboarding.

   Rendered INSTEAD of the shell, exactly like g-signin and for the same reason:
   there is no workspace underneath worth preserving, and an overlay would leave a
   tabbable rail and tab bar behind it. That choice is what keeps this out of the
   router entirely — it is a session state, not a route, so there is no
   WORKSPACES entry, no history entry and no tab to hide. See ADR 0014.

   Why it exists: the seed taxonomy in app/categories.py encodes the OWNER's
   interests, and the first saver's taxonomy drives the Gemini analysis enum while
   category edits are future-reels-only by design. A user who saves for a week
   before finding Settings has a permanently mis-bucketed library, so the taxonomy
   has to be chosen before the first reel lands.

   Three shapes worth not undoing:

   1. The chips WRITE INTO the textarea rather than tracking their own selection.
      One input the model reads, no hidden second source of truth, and the user can
      edit or delete anything a chip added. It also avoids teaching the shared
      g-chips a multi-select mode for one caller.

   2. Review looks like Settings > Categories — flat rows, inset rules, inline
      edit, an accent add row — because it IS that list, and Settings is where they
      will edit it next. Hence the settingsRow import: same treatment, and layout
      may depend on workspace (never the reverse).

   3. No --overhang here, unlike g-detail. That token exists for a position:fixed
      overlay escaping the shell's box. This lives INSIDE g-shell, which is
      height:100% of body precisely so its bottom is the genuinely visible bottom
      (ADR 0010) — so a footer at the end of this flex child is already above the
      nav bar. --safe-b still applies, for a home-bar cutout. */

import { LitElement, html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { shadowReset } from '../../design/base.js';
import { settingsRow } from '../../workspace/g-settings/row.js';
import {
  completeOnboarding,
  getOnboardingSuggestions,
  isSignedIn,
  proposeCategories,
} from '../../core/api.js';
import type { ProposedCategory } from '../../core/types.js';
import '../../component/g-button/g-button.js';
import '../../component/g-field/g-field.js';
import '../../component/g-icon/g-icon.js';
import '../../component/g-mark/g-mark.js';
import '../../component/g-notice/g-notice.js';

/** Floor on the "building…" indicator, so a fast failure can't flash and yank. */
const SPINNER_FLOOR_MS = 1000;

type Step = 'interests' | 'working' | 'review';

@customElement('g-onboarding')
export class GOnboarding extends LitElement {
  static override styles = [
    shadowReset,
    settingsRow,
    css`
      :host {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        background: var(--bg);
        padding-top: var(--safe-t);
      }
      /* The scrolling middle. The footer is a sibling, not sticky positioning:
         a flex column with one scrolling child pins it without taking it out of
         flow, so it can never overlap the last row. */
      .scroll {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
      }
      .pad {
        padding: var(--space-5) var(--content-r) var(--space-5) var(--content-l);
      }
      /* Desktop reads as a centred column; mobile is full-bleed, and --gutter-outer
         is already 0 there (ADR 0009's gutter handover), so this needs no
         breakpoint of its own. */
      .col {
        max-width: 560px;
        margin: 0 auto;
      }
      .brand {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        font-family: var(--font-display);
        font-size: 1.1rem;
        font-weight: 800;
        letter-spacing: -0.01em;
        --mark-size: 16px;
        user-select: none;
        margin-bottom: var(--space-5);
      }
      /* Four sizes only, matching ADR 0011's scale: 1.3 display (this is a hero,
         not a settings header), 0.95 row, 0.82 secondary. */
      h1 {
        margin: 0 0 var(--space-2);
        font-family: var(--font-display);
        font-size: 1.3rem;
        font-weight: 800;
        letter-spacing: -0.01em;
      }
      .sub {
        margin: 0 0 var(--space-4);
        color: var(--text-dim);
        font-size: 0.82rem;
        line-height: 1.5;
      }
      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
      }
      /* Local chip buttons, not g-chips: that component is single-select by
         contract and these append text instead of holding a selection.
         Full --touch-min tall rather than g-chips' 36px + ::after extension —
         that trick is gated to a NON-wrapping row for a reason (its own note:
         extending hit areas across wrapped rows makes them overlap invisibly
         between the rows). This cloud wraps, so the height has to be real. */
      .chip {
        min-height: var(--touch-min);
        padding: var(--space-2) var(--space-3);
        border: 1px solid var(--border);
        border-radius: var(--radius-full);
        background: var(--surface);
        color: var(--text);
        font: inherit;
        font-family: var(--font-ui);
        font-size: 0.82rem;
        cursor: pointer;
        transition: background-color 120ms ease;
      }
      .chip:active {
        background: var(--press);
      }
      @media (hover: hover) {
        .chip:hover {
          background: var(--surface-2);
        }
      }
      .chip:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }
      .chip[aria-pressed='true'] {
        border-color: var(--accent);
        color: var(--accent);
        font-weight: 700;
      }
      .count {
        margin: var(--space-1) 0 0;
        color: var(--text-dim);
        font-size: 0.75rem;
        font-family: var(--font-mono);
        text-align: right;
      }
      .count[data-over] {
        color: var(--danger);
      }

      /* ── working ── */
      .working {
        display: grid;
        place-items: center;
        gap: var(--space-3);
        padding: var(--space-6) 0;
        text-align: center;
      }
      .spinner {
        width: 28px;
        height: 28px;
        border: 3px solid var(--border);
        border-top-color: var(--accent);
        border-radius: var(--radius-full);
        animation: spin 700ms linear infinite;
      }
      @keyframes spin {
        to {
          transform: rotate(360deg);
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .spinner {
          animation-duration: 2400ms;
        }
      }
      .working .what {
        font-size: 0.95rem;
      }
      .working .why {
        color: var(--text-dim);
        font-size: 0.82rem;
      }

      /* ── review ── */
      /* Rows are full-bleed on mobile (the .pad inset is cancelled) so they read
         as one list rather than a floating stack — the same handover Settings
         does. .row/.sep come from settingsRow. */
      .list {
        margin: 0 calc(var(--content-r) * -1) 0 calc(var(--content-l) * -1);
      }
      .item {
        display: flex;
        align-items: flex-start;
        gap: var(--space-3);
        width: 100%;
        padding: var(--space-3) var(--row-pad-r)
          var(--space-3) var(--row-pad-l);
        min-height: var(--touch-min);
        text-align: left;
      }
      .itemMain {
        flex: 1;
        min-width: 0;
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        font-family: var(--font-ui);
        text-align: left;
        cursor: pointer;
        padding: 0;
      }
      .itemMain:focus-visible {
        outline: none;
        box-shadow: var(--ring);
      }
      .name {
        font-size: 0.95rem;
      }
      .desc {
        margin-top: 2px;
        color: var(--text-dim);
        font-size: 0.82rem;
        line-height: 1.45;
      }
      .edit {
        display: grid;
        gap: var(--space-2);
        padding: var(--space-3) var(--row-pad-r)
          var(--space-3) var(--row-pad-l);
      }
      .editActs {
        display: flex;
        justify-content: flex-end;
        gap: var(--space-2);
      }
      .hint {
        color: var(--text-dim);
        font-size: 0.82rem;
      }
      .newRow {
        color: var(--accent);
        font-weight: 700;
        --icon-size: 16px;
      }
      .cap {
        margin: var(--space-4) 0 0;
        color: var(--text-dim);
        font-size: 0.82rem;
      }

      /* ── footer ── */
      /* A sibling of .scroll, so it is pinned by the flex column rather than by
         position:sticky. --safe-b covers a home-bar cutout; the nav-bar overhang
         is already handled by g-shell being height:100% (see the header note). */
      footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-3);
        padding: var(--space-3) var(--content-r)
          calc(var(--space-3) + var(--safe-b)) var(--content-l);
        border-top: 1px solid var(--border);
        background: var(--bg);
      }
      /* g-button defaults to 40.5px on a coarse pointer — its inner min-height:0
         is deliberate, so that the CALLER can pin the host's height (its own
         note). These are the primary controls on a touch-first screen, so they
         take the floor. App-wide g-button sizing is left alone on purpose. */
      footer g-button,
      .editActs g-button {
        min-height: var(--touch-min);
      }
      .footInner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-3);
        width: 100%;
        max-width: 560px;
        margin: 0 auto;
      }
      g-notice {
        margin-bottom: var(--space-3);
      }
    `,
  ];

  @state() private step: Step = 'interests';
  @state() private suggestions: string[] = [];
  @state() private maxLength = 500;
  @state() private interests = '';
  @state() private proposed: ProposedCategory[] = [];
  @state() private editingIndex: number | null = null;
  @state() private adding = false;
  @state() private draftName = '';
  @state() private draftDesc = '';
  @state() private error = '';
  @state() private quotaExhausted = false;
  @state() private busy = false;

  override connectedCallback(): void {
    super.connectedCallback();
    void this.loadSuggestions();
  }

  private async loadSuggestions(): Promise<void> {
    const r = await getOnboardingSuggestions();
    // A failure here is survivable — the textarea alone still works, so there is
    // nothing worth telling the user about.
    if (!isSignedIn(r)) return;
    this.suggestions = r.suggestions;
    this.maxLength = r.max_length;
  }

  /** Chips are additive text, not a selection — see the header note. */
  private toggleChip(label: string): void {
    const parts = this.interests
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    const at = parts.findIndex((p) => p.toLowerCase() === label.toLowerCase());
    if (at >= 0) parts.splice(at, 1);
    else parts.push(label);
    this.interests = parts.join(', ');
  }

  private chipOn(label: string): boolean {
    return this.interests
      .split(',')
      .some((p) => p.trim().toLowerCase() === label.toLowerCase());
  }

  private get over(): boolean {
    return this.interests.length > this.maxLength;
  }

  private async propose(): Promise<void> {
    const interests = this.interests.trim();
    if (!interests || this.over) return;
    this.error = '';
    this.step = 'working';
    // Race the request against the floor and take the later of the two, so the
    // indicator is always legible even when the failure is instant.
    const started = Date.now();
    try {
      const r = await proposeCategories(interests);
      const wait = SPINNER_FLOOR_MS - (Date.now() - started);
      if (wait > 0) await new Promise((res) => setTimeout(res, wait));
      if (!isSignedIn(r)) return;
      this.quotaExhausted = r.quota_exhausted;
      this.proposed = r.categories;
      this.step = 'review';
    } catch (e) {
      const wait = SPINNER_FLOOR_MS - (Date.now() - started);
      if (wait > 0) await new Promise((res) => setTimeout(res, wait));
      this.error = e instanceof Error ? e.message : String(e);
      this.step = 'interests';
    }
  }

  /** `null` categories is the Skip path — the server keeps the seeded defaults. */
  private async finish(categories: ProposedCategory[] | null): Promise<void> {
    this.busy = true;
    this.error = '';
    try {
      const r = await completeOnboarding(this.interests.trim(), categories);
      if (!isSignedIn(r)) return;
      this.dispatchEvent(new CustomEvent('done', { bubbles: true, composed: true }));
    } catch (e) {
      this.error = e instanceof Error ? e.message : String(e);
    } finally {
      this.busy = false;
    }
  }

  private startEdit(i: number): void {
    this.adding = false;
    this.editingIndex = i;
    this.draftName = this.proposed[i]?.name ?? '';
    this.draftDesc = this.proposed[i]?.description ?? '';
  }

  private startAdd(): void {
    this.editingIndex = null;
    this.adding = true;
    this.draftName = '';
    this.draftDesc = '';
  }

  private commitDraft(): void {
    const name = this.draftName.trim();
    if (!name) return;
    const entry = { name, description: this.draftDesc.trim() };
    const next = [...this.proposed];
    if (this.editingIndex !== null) next[this.editingIndex] = entry;
    else next.push(entry);
    this.proposed = next;
    this.editingIndex = null;
    this.adding = false;
  }

  /** `removeAt`, not `remove` — HTMLElement.remove() is already taken. */
  private removeAt(i: number): void {
    this.proposed = this.proposed.filter((_, x) => x !== i);
    if (this.editingIndex === i) this.editingIndex = null;
  }

  private renderEditor(onCancel: () => void) {
    return html`
      <div class="edit">
        <g-field
          shape="box"
          label="Name"
          .value=${this.draftName}
          .debounce=${0}
          @g-input=${(e: CustomEvent<string>) => (this.draftName = e.detail)}
        ></g-field>
        <g-field
          shape="box"
          label="Description"
          placeholder="what belongs in this category…"
          .value=${this.draftDesc}
          .debounce=${0}
          @g-input=${(e: CustomEvent<string>) => (this.draftDesc = e.detail)}
        ></g-field>
        <p class="hint">Helps decide what belongs here.</p>
        <div class="editActs">
          <g-button @click=${onCancel}>Cancel</g-button>
          <g-button variant="primary" @click=${this.commitDraft}>Done</g-button>
        </div>
      </div>
    `;
  }

  private renderInterests() {
    return html`
      <h1>What do you save?</h1>
      <p class="sub">
        Tap what fits or write your own. We'll build your categories from it — you can
        change them anytime.
      </p>

      <div class="chips">
        ${this.suggestions.map(
          (s) => html`<button
            class="chip"
            type="button"
            aria-pressed=${this.chipOn(s) ? 'true' : 'false'}
            @click=${() => this.toggleChip(s)}
          >${s}</button>`,
        )}
      </div>

      <g-field
        shape="box"
        multiline
        label="Your interests"
        placeholder="e.g. skincare routines, wedding outfits, travel food guides…"
        .value=${this.interests}
        .debounce=${0}
        @g-input=${(e: CustomEvent<string>) => (this.interests = e.detail)}
      ></g-field>
      <p class="count" ?data-over=${this.over}>
        ${this.interests.length}/${this.maxLength}
      </p>
    `;
  }

  private renderReview() {
    return html`
      <h1>Your categories</h1>
      <p class="sub">
        Edit or remove any. You can change these anytime in Settings.
      </p>

      ${this.quotaExhausted
        ? html`<g-notice tone="warn">
            Couldn't build these right now — the daily AI quota is used up. We've
            started you with a general set; edit it anytime in Settings.
          </g-notice>`
        : nothing}

      <div class="list">
        ${this.proposed.map((c, i) =>
          this.editingIndex === i
            ? html`<div class=${i ? 'sep' : ''}>
                ${this.renderEditor(() => (this.editingIndex = null))}
              </div>`
            : html`<div class="item ${i ? 'sep' : ''}">
                <button class="itemMain" @click=${() => this.startEdit(i)}>
                  <div class="name">${c.name}</div>
                  ${c.description ? html`<div class="desc">${c.description}</div>` : nothing}
                </button>
                <g-button
                  icon-only
                  label=${`Remove ${c.name}`}
                  @click=${() => this.removeAt(i)}
                ><g-icon name="x"></g-icon></g-button>
              </div>`,
        )}
        ${this.adding
          ? html`<div class="sep">${this.renderEditor(() => (this.adding = false))}</div>`
          : html`<button class="row sep newRow" @click=${this.startAdd}>
              <g-icon name="plus"></g-icon>Add another
            </button>`}
      </div>

      <p class="cap">“Other” is always kept as a fallback.</p>
    `;
  }

  override render() {
    const working = this.step === 'working';
    return html`
      <div class="scroll">
        <div class="pad">
          <div class="col">
            <div class="brand"><g-mark></g-mark>grain</div>
            ${this.error ? html`<g-notice tone="danger">${this.error}</g-notice>` : nothing}
            ${working
              ? html`<div class="working">
                  <div class="spinner" role="status" aria-label="Building your categories"></div>
                  <div class="what">Building your categories…</div>
                  <div class="why">This takes a few seconds.</div>
                </div>`
              : this.step === 'interests'
                ? this.renderInterests()
                : this.renderReview()}
          </div>
        </div>
      </div>

      ${working
        ? nothing
        : html`<footer>
            <div class="footInner">
              <g-button ?disabled=${this.busy} @click=${() => this.finish(null)}>
                Skip for now
              </g-button>
              ${this.step === 'interests'
                ? html`<g-button
                    variant="primary"
                    ?disabled=${!this.interests.trim() || this.over}
                    @click=${this.propose}
                  >Continue</g-button>`
                : html`<g-button
                    variant="primary"
                    ?disabled=${this.busy || this.proposed.length === 0}
                    @click=${() => this.finish(this.proposed)}
                  >${this.busy ? 'Saving…' : 'Use these'}</g-button>`}
            </div>
          </footer>`}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'g-onboarding': GOnboarding;
  }
}
